import { NodeResizer, type NodeProps } from '@xyflow/react';

export interface GroupNodeData {
    label: string;
    [key: string]: unknown;
}

/**
 * Group Node
 *
 * A visual-only frame (doc 06 §1 specifies "0/0" pins — not a story-flow
 * participant, just an organizational container via React Flow's
 * `parentId` subflow feature). An earlier version rendered live
 * target/source `Handle`s, contradicting that spec and letting users draw
 * nonsensical connections into/out of a purely visual frame — removed.
 *
 * The `parentId` wiring (translating a member node's `data.parent_uuid`
 * to/from React Flow's `parentId` on load/save, plus drag-to-group
 * detection) is implemented in projectStore.ts's `openGraph`/`saveGraph`
 * and GraphCanvas.tsx's `onNodeDragStop` — dragging this frame does move
 * its members.
 */
export function GroupNode({ data, selected }: NodeProps) {
    const nodeData = data as GroupNodeData;

    return (
        <div
            className="d-flex flex-column"
            style={{
                width: '100%',
                height: '100%',
                minWidth: 200,
                minHeight: 150,
                // Previously a hardcoded, unconditional border regardless of
                // `selected` — every other node type dims/highlights its
                // border on selection, but this one gave zero visual
                // feedback beyond the resize handles appearing.
                border: selected ? '2px dashed var(--tblr-primary)' : '2px dashed var(--tblr-border-color)',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                padding: '8px',
                boxShadow: selected ? '0 0 0 2px rgba(var(--tblr-primary-rgb), 0.15)' : undefined,
            }}
        >
            {selected && <NodeResizer />}
            <div className="small text-muted fw-bold nodrag">{nodeData.label || 'Group'}</div>
        </div>
    );
}

export const groupNodeMeta = {
    type: 'group' as const,
    label: 'Group',
    icon: 'ti-folder',
    defaultData: { label: 'Group' } satisfies GroupNodeData,
};
